import React                             from 'react';
import View                              from '@widesk-ui/components/View';
import { observer }                      from 'mobx-react-lite';
import { TEXT_FONTS }                    from '@tokamap/core';
import ColorButton                       from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import Segmented                         from '@widesk-ui/Segmented';
import { DEFAULT_TEXT_COLOR }            from '@/TokamapEditor/ux/PreviewLetter';
import TextContentField                  from '@/TokamapEditor/ux/AreaSidebar/TextContentField';
import { NormalHoverScope }              from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import { ImposedField }                  from '@/TokamapEditor/ux/ThemeFields';
import { ScopeToggle }                   from '@/TokamapEditor/ux/ThemeFields';
import { ThemeSlider }                   from '@/TokamapEditor/ux/ThemeFields';
import { ThemeShareSlider }              from '@/TokamapEditor/ux/ThemeFields';
import { ThemePositionField }            from '@/TokamapEditor/ux/ThemeFields';
import { ThemeOrientationField }         from '@/TokamapEditor/ux/ThemeFields';
import themeStore                        from '@/TokamapEditor/stores/themeStore';
import { Theme }                         from '@/TokamapEditor/stores/themeStore';
import { ThemeTextStyle }                from '@/TokamapEditor/stores/themeStore';
import { DEFAULT_TEXT_FIXED_SIZE_PX }    from '@/TokamapEditor/ux/constants';
import { SLIDER_MAX_TEXT_FIXED_SIZE_PX } from '@/TokamapEditor/ux/constants';
import { MAX_TEXT_FIXED_SIZE_PX }        from '@/TokamapEditor/ux/constants';
import TextFontSelect                    from '@/TokamapEditor/ux/TextFontSelect';

/** Contour de départ d'un contour tout juste imposé : blanc, le halo usuel d'un texte sur la carte. */
const IMPOSED_OUTLINE_START = '#ffffff';

const ThemeTextFields = observer(({ theme, save, initialScope = 'normal' }: {
	theme: Theme;
	save: (task: Promise<unknown>) => Promise<void>;
	initialScope?: NormalHoverScope;
}) => {
	const [scope, setScope] = React.useState<NormalHoverScope>(initialScope);
	const text = theme.text[scope];
	const set = (style: ThemeTextStyle) => save(themeStore.setThemeText(theme.id, scope, style));
	const fixedSize = text.fixedSizePx !== undefined;

	return (
		<View gap="md">
			<ScopeToggle value={scope} onChange={setScope} />

			<ThemeTextContentField key={scope} theme={theme} save={save} scope={scope} />

			<ImposedField
				field="color"
				label="Couleur du texte"
				imposed={text.color !== undefined}
				onImpose={() => set({ color: DEFAULT_TEXT_COLOR })}
				onRelease={() => set({ color: undefined })}
			>
				<ColorButton label="Texte" value={text.color} allowClear={false} onChange={color => set({ color })} />
			</ImposedField>

			<ImposedField
				field="bgColor"
				label="Couleur du contour"
				imposed={text.bgColor !== undefined}
				onImpose={() => set({ bgColor: IMPOSED_OUTLINE_START })}
				onRelease={() => set({ bgColor: undefined })}
			>
				<ColorButton label="Contour" value={text.bgColor} allowClear={false} onChange={bgColor => set({ bgColor })} />
			</ImposedField>

			<ImposedField
				field="font"
				label="Police"
				imposed={text.font !== undefined}
				onImpose={() => set({ font: TEXT_FONTS[0]!.id })}
				onRelease={() => set({ font: undefined })}
			>
				<TextFontSelect value={text.font} withDefault={false} onChange={font => set({ font: font ?? TEXT_FONTS[0]!.id })} />
			</ImposedField>

			<ImposedField
				field="fontStyle"
				label="Style de police"
				imposed={text.fontStyle !== undefined}
				onImpose={() => set({ fontStyle: 'regular' })}
				onRelease={() => set({ fontStyle: undefined })}
			>
				<Segmented
					block
					size="small"
					value={text.fontStyle}
					onChange={fontStyle => set({ fontStyle: fontStyle as ThemeTextStyle['fontStyle'] })}
					options={[
						{ value: 'regular', label: 'Normal' },
						{ value: 'bold', label: 'Gras' },
						{ value: 'italic', label: 'Italique' },
					]}
				/>
			</ImposedField>

			<ImposedField
				field="uppercase"
				label="Majuscules"
				imposed={text.uppercase !== undefined}
				onImpose={() => set({ uppercase: false })}
				onRelease={() => set({ uppercase: undefined })}
			>
				<Segmented
					block
					size="small"
					value={text.uppercase ? 'upper' : 'asTyped'}
					onChange={value => set({ uppercase: value === 'upper' })}
					options={[
						{ value: 'asTyped', label: 'Telles que saisies' },
						{ value: 'upper', label: 'Tout en majuscules' },
					]}
				/>
			</ImposedField>

			<ImposedField
				field="size"
				label="Taille"
				imposed={fixedSize || text.size !== undefined}
				onImpose={() => set({ size: 1, fixedSizePx: undefined })}
				onRelease={() => set({ size: undefined, fixedSizePx: undefined })}
			>
				<ThemeSlider
					value={fixedSize ? text.fixedSizePx! : Math.round((text.size ?? 1) * 100)}
					min={1}
					max={fixedSize ? SLIDER_MAX_TEXT_FIXED_SIZE_PX : 100}
					inputMax={fixedSize ? MAX_TEXT_FIXED_SIZE_PX : 100}
					step={1}
					unitSelect={{
						value: fixedSize ? 'px' : '%',
						options: [
							{ value: '%', label: '%' },
							{ value: 'px', label: 'px' },
						],
						title: '% : proportionnelle à la forme. px : taille fixe à l\'écran, quel que soit le zoom.',
						onChange: unit => set(unit === 'px'
							? { size: undefined, fixedSizePx: DEFAULT_TEXT_FIXED_SIZE_PX }
							: { size: 1, fixedSizePx: undefined }),
					}}
					onSave={value => set(fixedSize ? { fixedSizePx: value } : { size: value / 100 })}
				/>
			</ImposedField>

			<ImposedField
				field="opacity"
				label="Opacité"
				imposed={text.opacity !== undefined}
				onImpose={() => set({ opacity: 1 })}
				onRelease={() => set({ opacity: undefined })}
			>
				<ThemeShareSlider value={text.opacity ?? 1} onSave={opacity => set({ opacity })} />
			</ImposedField>

			<ThemePositionField value={text} onChange={set} />

			{/* Le texte au survol garde l'orientation du texte normal. */}
			{scope === 'normal' && <ThemeOrientationField readable value={text} onChange={set} />}
		</View>
	);
});

/** Le texte imposé aux formes du thème : dans la forme, ou dans son marqueur, qui ne lit que l'état normal. */
export const ThemeTextContentField = observer(({ theme, save, scope = 'normal', label = 'Texte' }: {
	theme: Theme;
	save: (task: Promise<unknown>) => Promise<void>;
	scope?: NormalHoverScope;
	label?: string;
}) => {
	const text = theme.text[scope];
	const set = (style: ThemeTextStyle) => save(themeStore.setThemeText(theme.id, scope, style));
	const [writing, setWriting] = React.useState(false);

	return (
		<ImposedField
			field="text"
			label={label}
			imposed={text.text !== undefined || writing}
			onImpose={() => setWriting(true)}
			onRelease={() => {
				setWriting(false);
				set({ text: undefined });
			}}
		>
			<TextContentField value={text.text} onSubmit={value => set({ text: value.trim() ? value : undefined })} />
		</ImposedField>
	);
});

export default ThemeTextFields;
